import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";

// Component
import PanelProjectList from "./PanelProjectList";
import PanelCreateProject from "./PanelCreateProject";

// Interface
import {
  CARD_NAME,
  MasterOptions,
  OnEvent,
  PickedState,
  RunSequence,
  SetProp,
  State,
  ProjectListType,
} from "./sequence/ProjectList";
import { IssueListType } from "./sequence/IssueList";

// type
type CardProjectListProps = {
  onEvent: OnEvent;
  setProp: SetProp;
  runSequence: RunSequence;
  ProjectListSequence: State["ProjectListSequence"];
  masterOptions: MasterOptions;
  languageUX: "en" | "th";
} & Pick<PickedState, "buttonLoadCheck" | "errorMessage" | "successMessage">;

type IssueType = {
  id: number;
  tracker: {
    id: number;
    name: string;
  };
  status: {
    id: number;
    name: string;
    is_closed: boolean;
  };
};

const CardProjectList = (props: CardProjectListProps) => {
  const [formState, setFormState] = useState<Partial<ProjectListType>>({});
  const [searchProject, setSearchProject] = useState<string | undefined>(undefined);

  const defaultFormState: Partial<ProjectListType> = {
    created_on: undefined,
    description: undefined,
    homepage: undefined,
    id: undefined,
    identifier: undefined,
    inherit_members: undefined,
    is_public: undefined,
    name: undefined,
    status: undefined,
    updated_on: undefined,
    custom_fields: [...(formState.custom_fields?.filter((f) => f.name !== "Division") || [])],
  };

  useEffect(() => {
    props.runSequence({ sequence: "ProjectList", restart: true });
  }, []);

  // Clear Data When Save Success
  useEffect(() => {
    if (props.successMessage?.[CARD_NAME]) {
      setFormState(defaultFormState);
    }
  }, [props.successMessage]);

  const projectList = useMemo(() => {
    return (props.ProjectListSequence?.projectList || []).filter(
      (project) => !!project.parent
    );
  }, [props.ProjectListSequence?.projectList]);

  const issuesTracking = useMemo(
    () => summarizedIssues(props.ProjectListSequence?.issuesTracking || []),
    [props.ProjectListSequence?.issuesTracking]
  );

  const updateCustomField = (name: string, value: any) => {
    const newCustomFields = [...(formState.custom_fields || [])];
    const index = newCustomFields.findIndex((f) => f.name === name);

    if (index !== -1) {
      newCustomFields[index] = {
        ...newCustomFields[index],
        value,
      };
    } else {
      newCustomFields.push({ name, value });
    }

    return newCustomFields;
  };

  const handleChangeData = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const { name, value } = data;

    const isCustomFields = ["Division", "Active"].includes(name);

    if (isCustomFields) {
      const newCustomFields = updateCustomField(name, value);

      setFormState({
        ...formState,
        custom_fields: newCustomFields,
      });

      return;
    }

    setFormState({
      ...formState,
      [name]: value,
    });
  };

  const handleChangeSearch = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const { value } = data;

    setSearchProject(value);
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "ProjectList",
      action: "SEARCH",
      project: searchProject,
    });
  };

  const handleSelectProject = (project: Partial<ProjectListType>) => {
    setFormState(project);

    if (!project.id) {
      return;
    }

    props.runSequence({
      sequence: "ProjectList",
      action: "DETAIL",
      projectId: project.id,
      // limit: 9999,
    });
  };

  const handleClear = () => {
    // setSearchProject(undefined);
    setFormState(defaultFormState);
  };

  const handleCreate = () => {
    props.runSequence({
      sequence: "ProjectList",
      action: "CREATE",
      createdData: formState,
    });
  };

  const handleEdit = () => {
    if (!formState.id) {
      return;
    }

    props.runSequence({
      sequence: "ProjectList",
      action: "EDIT",
      projectId: formState.id,
      editedData: formState,
    });
  };

  const handleInactive = () => {
    if (!formState.id) {
      return;
    }

    const updatedFields = updateCustomField("Active", "0");

    props.runSequence({
      sequence: "ProjectList",
      action: "EDIT",
      projectId: formState.id,
      editedData: {
        ...formState,
        custom_fields: updatedFields,
      }
    });
  };

  const handleActive = () => {
    if (!formState.id) {
      return;
    }

    const updatedFields = updateCustomField("Active", "1");

    props.runSequence({
      sequence: "ProjectList",
      action: "EDIT",
      projectId: formState.id,
      editedData: {
        ...formState,
        custom_fields: updatedFields,
      }
    });
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "grid",
          position: "relative",
          gridTemplateRows: "repeat(1, 1fr)",
          gridTemplateColumns: "repeat(3, 1fr)",
        }}
      >
        <div style={{ gridRow: "1/2", gridColumn: "1/2", overflow: "auto" }}>
          <PanelProjectList
            setProp={props.setProp}
            // search section
            searchValue={searchProject}
            onSearch={handleSearch}
            onChangeSearch={handleChangeSearch}
            // select project section
            onSelectProject={handleSelectProject}
            data={projectList}
            selectedProject={formState.id || null}
            buttonLoadCheck={props.buttonLoadCheck}
          />
        </div>
        <div style={{ gridRow: "1/2", gridColumn: "2/4", height: "100%", overflow: "auto" }}>
          <PanelCreateProject
            data={formState}
            tableData={issuesTracking}
            onChange={handleChangeData}
            onCreate={handleCreate}
            onEdit={handleEdit}
            onClear={handleClear}
            onActive={handleActive}
            onInactive={handleInactive}
            setProp={props.setProp}
            masterOptions={props.masterOptions}
            buttonLoadCheck={props.buttonLoadCheck}
          />
        </div>
      </div>
    </div>
  );
};

/*================================ FORMATTER ==============================*/

const summarizedIssues = (issues: IssueListType[]) => {
  if (!issues) return [];

  const summaryMap: Record<
    string,
    {
      tracker: string;
      open: number;
      resolved: number;
      closed: number;
      total: number;
    }
  > = {};

  for (const issue of issues) {
    const trackerName = issue.tracker?.name || "Unknown";
    const statusName = issue.status?.name || "";
    const isClosed = issue.status?.is_closed;

    if (!summaryMap[trackerName]) {
      summaryMap[trackerName] = {
        tracker: trackerName,
        open: 0,
        resolved: 0,
        closed: 0,
        total: 0,
      };
    }

    if (!isClosed) {
      summaryMap[trackerName].open++;
    } else {
      if (statusName.toLowerCase() === "resolved") {
        summaryMap[trackerName].resolved++;
      } else {
        summaryMap[trackerName].closed++;
      }
    }

    summaryMap[trackerName].total++;
  }

  return Object.values(summaryMap);
};

export default CardProjectList;
